import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCalendar,
  PiCheckCircle,
  PiClock,
  PiDotsThreeVerticalBold,
  PiEnvelope,
  PiGear,
  PiGlobe,
  PiPause,
  PiPencilSimple,
  PiPlay,
  PiUser,
} from 'react-icons/pi'
import { Spinner } from 'react-bootstrap'

import { cn } from 'components/UI/cn'
import { NotFoundContent } from '../not-found-content'
import { WORKFLOW_STATUS } from 'models/campaign'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import {
  usePauseCampaign,
  useResumeCampaign,
  useGetCampaignSettings,
} from 'services/campaigns'
import { useGetReplyThreadSummary } from 'features/campaigns/replies_api'
import {
  useArchiveCampaign,
  useGetCampaignById,
  useUnarchiveCampaign,
} from 'features/campaigns/campaigns_api'
import { ArchiveCampaignModal } from 'features/campaigns/ArchiveCampaignModal'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { CampaignDashboardOverviewTab } from 'features/campaigns/CampaignDashboardOverviewTab'
import { CampaignDashboardTargetsTab } from 'features/campaigns/CampaignDashboardTargetsTab'
import { CampaignDashboardContactsTab } from 'features/campaigns/CampaignDashboardContactsTab'
import { useInvalidateCampaignContactRequests } from 'services/contactRequestApi'
import { CampaignDashboardDealsTab } from 'features/campaigns/CampaignDashboardDealsTab'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { useEntityBreadcrumbs } from 'context/layout'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTitleRow,
  FBPageTitle,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'

const DASHBOARD_TAB_KEYS = [
  'overview',
  'targets',
  'contacts',
  'deals',
  'replies',
] as const
type DashboardTab = (typeof DASHBOARD_TAB_KEYS)[number]

function isDashboardTab(value: string | null): value is DashboardTab {
  return (
    value !== null && (DASHBOARD_TAB_KEYS as readonly string[]).includes(value)
  )
}

function getStatusBadge(status: string): {
  label: string
  className: string
} {
  switch (status) {
    case WORKFLOW_STATUS.DRAFT:
    case 'draft':
      return {
        label: 'Draft',
        className: 'bg-gray-100 text-gray-700',
      }
    case WORKFLOW_STATUS.ACTIVE:
      return {
        label: 'Active',
        className: 'bg-success-bg-subtle text-success-strong',
      }
    case WORKFLOW_STATUS.PAUSED:
      return {
        label: 'Paused',
        className: 'bg-warning-bg-subtle text-warning-strong',
      }
    case WORKFLOW_STATUS.COMPLETE:
    case 'completed':
      return {
        label: 'Complete',
        className: 'bg-success-bg-subtle text-success-strong',
      }
    case 'archived':
      return {
        label: 'Archived',
        className: 'bg-neutral-100 text-neutral-700',
      }
    case 'deleted':
      return {
        label: 'Deleted',
        className: 'bg-neutral-100 text-neutral-700',
      }
    case 'launching':
      return {
        label: 'Launching',
        className: 'bg-info-bg-subtle text-info-strong',
      }
    case 'queued':
      return {
        label: 'Queued',
        className: 'bg-info-bg-subtle text-info-strong',
      }
    case 'failed':
      return {
        label: 'Failed',
        className: 'bg-error-bg-subtle text-error-strong',
      }
    case 'pending deletion':
      return {
        label: 'Pending Deletion',
        className: 'bg-error-bg-subtle text-error-strong',
      }
    default:
      return {
        label: status,
        className: 'bg-neutral-100 text-fg-secondary',
      }
  }
}

function sumStepWaitDays(steps: { wait_in_days?: number }[]): number {
  if (!steps || steps.length === 0) return 0
  return steps.reduce((total, step) => total + (step.wait_in_days ?? 0), 0)
}

function formatDurationDays(days: number): string {
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''}`
  }
  const weeks = Math.floor(days / 7)
  const remainingDays = days % 7
  if (remainingDays === 0) {
    return `${weeks} week${weeks !== 1 ? 's' : ''}`
  }
  return `${weeks} week${weeks !== 1 ? 's' : ''} ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`
}

export function CampaignDashboardPageV2() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaignId = Number(params.id)

  const { data: campaign, isLoading: campaignsLoading } =
    useGetCampaignById(campaignId)

  const breadcrumbs = useMemo(
    () => (campaign?.name ? [{ label: campaign.name }] : []),
    [campaign?.name]
  )
  useEntityBreadcrumbs(breadcrumbs)

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0

  const { data: replySummary } = useGetReplyThreadSummary({
    campaign_id: bisonCampaignId,
  })
  const { data: settings } = useGetCampaignSettings(campaignId, !!campaign)

  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()
  const handleContactRequested =
    useInvalidateCampaignContactRequests(campaignId)

  const [searchParams] = useSearchParams()
  // Read ?tab= once on mount so subsequent in-app tab clicks aren't
  // overridden by a stale URL. The ?thread= param is owned by
  // CampaignRepliesTab itself (it also clears it when the user dismisses
  // the linked-thread filter).
  const initialTab = useMemo<DashboardTab>(() => {
    const t = searchParams.get('tab')
    return isDashboardTab(t) ? t : 'overview'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab)
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()

  if (campaignsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!campaign) {
    return <NotFoundContent />
  }

  const campaignStatus = settings?.status || campaign.workflow_status
  const isPaused = campaignStatus === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaign.is_archived ||
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaignStatus === WORKFLOW_STATUS.COMPLETE
  const canControl = !isComplete
  const badge = getStatusBadge(campaignStatus)
  const emailStepsCount = sequence?.steps?.length ?? 0
  const estimatedDays = sequence?.steps ? sumStepWaitDays(sequence.steps) : 0
  const isActiveCampaign =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'launching'
  const sequenceButtonLabel =
    isComplete || isActiveCampaign ? 'View Sequence' : 'Edit Sequence'

  const tabCounts = {
    targets: campaign.contact_company_count ?? 0,
    contacts: campaign.contact_count ?? 0,
    deals: campaign.deal_count ?? 0,
    replies: replySummary?.is_available ? (replySummary?.total ?? 0) : 0,
  }

  const dashboardTabs: {
    key: DashboardTab
    title: string
    count?: number | null
  }[] = [
    { key: 'overview', title: 'Overview' },
    { key: 'targets', title: 'Targets', count: tabCounts.targets },
    { key: 'contacts', title: 'Contacts', count: tabCounts.contacts },
    { key: 'deals', title: 'Deals', count: tabCounts.deals },
    { key: 'replies', title: 'Replies', count: tabCounts.replies },
  ]

  const handlePauseResume = () => {
    const onError = () =>
      toast.error(`Failed to ${isPaused ? 'resume' : 'pause'} campaign`)
    if (isPaused) {
      resumeMutation.mutate(
        { bison_campaign_id: bisonCampaignId, campaign_id: campaignId },
        { onError }
      )
    } else {
      pauseMutation.mutate(
        { bison_campaign_id: bisonCampaignId, campaign_id: campaignId },
        { onError }
      )
    }
  }

  return (
    <>
      {isComplete && (
        <div
          className={cn(
            'border-b',
            campaign.is_archived
              ? 'border-alert-default-border bg-alert-default-bg'
              : 'border-alert-success-border bg-alert-success-bg'
          )}
        >
          <div className="flex items-center py-3 pl-[calc(var(--page-vertical-padding)+var(--page-container-horizontal-padding))] pr-[var(--page-vertical-padding)]">
            <div
              className={cn(
                'flex items-center gap-2 text-sm font-medium tracking-[-0.2px]',
                campaign.is_archived
                  ? 'text-alert-default-title'
                  : 'text-alert-success-title'
              )}
            >
              {campaign.is_archived ? (
                <PiArchive className="size-4" />
              ) : (
                <PiCheckCircle className="size-4" />
              )}
              {campaign.is_archived
                ? 'This campaign has been archived.'
                : 'This campaign has been completed.'}
            </div>
          </div>
        </div>
      )}

      <FBPage
        innerPageClassName="!bg-[#fefefe]"
        containerClassName="!bg-[#fefefe] !shadow-none"
      >
        <FBPageHeader>
          <div className="flex flex-col items-start gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-2xl border-0 px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                badge.className
              )}
              style={
                campaignStatus === 'draft'
                  ? { backgroundColor: '#f3f4f6', color: '#374151' }
                  : undefined
              }
            >
              {badge.label}
            </span>
            <FBPageTitleRow className="w-auto">
              <FBPageTitle>
                {campaign.name}
                {campaign.type && (
                  <span className="ml-2 align-middle text-base font-normal text-gray-500">
                    · {campaign.type.name}
                  </span>
                )}
              </FBPageTitle>
            </FBPageTitleRow>
          </div>
          <div className="flex items-center gap-2">
            <FbButton
              onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
              className="!bg-brand-bright !text-white hover:!bg-brand-active whitespace-nowrap"
            >
              {isComplete ? (
                <PiEnvelope className="size-4" />
              ) : (
                <PiPencilSimple className="size-4" />
              )}
              <span>{sequenceButtonLabel}</span>
            </FbButton>
            {canControl && (
              <FbButton
                onClick={handlePauseResume}
                disabled={pauseMutation.isPending || resumeMutation.isPending}
                variant="secondary"
                className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900 whitespace-nowrap"
              >
                {isPaused ? (
                  <PiPlay className="size-4" />
                ) : (
                  <PiPause className="size-4" />
                )}
                <span>{isPaused ? 'Resume' : 'Pause'}</span>
              </FbButton>
            )}

            <div className="relative">
              <FbButton
                onClick={() => setShowOverflowMenu((prev) => !prev)}
                variant="secondary"
                className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900 !px-2.5"
                aria-label="More actions"
              >
                <PiDotsThreeVerticalBold className="size-4" />
              </FbButton>

              {showOverflowMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOverflowMenu(false)}
                  />
                  <div className="absolute right-0 top-full z-20 mt-1.5 w-48 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false)
                        setShowEditModal(true)
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                    >
                      <PiGear className="size-4 text-fg-secondary" />
                      Edit Campaign
                    </button>
                    {campaign.is_archived ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          unarchiveMutation.mutate(campaignId)
                        }}
                        disabled={unarchiveMutation.isPending}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <PiArrowCounterClockwise className="size-4 text-fg-secondary" />
                        Unarchive Campaign
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          setShowArchiveConfirm(true)
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <PiArchive className="size-4 text-fg-secondary" />
                        Archive Campaign
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </FBPageHeader>

        <div className="flex flex-wrap items-center gap-6 mb-4">
          {campaign.launch_date && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiCalendar className="size-4 text-brand-active" />
              Projected:{' '}
              <span className="font-medium text-gray-900">
                {new Date(campaign.launch_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </span>
          )}

          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiGlobe className="size-4 text-brand-active" />
            Sequence Name:{' '}
            <span className="font-medium text-gray-900">
              {settings?.name ?? '—'}
            </span>
          </span>

          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiEnvelope className="size-4 text-brand-active" />
            Email Steps:{' '}
            <span className="font-medium text-gray-900">{emailStepsCount}</span>
          </span>

          {estimatedDays > 0 && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiClock className="size-4 text-brand-active" />
              Est. Duration:{' '}
              <span className="font-medium text-gray-900">
                {formatDurationDays(estimatedDays)}
              </span>
            </span>
          )}

          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiUser className="size-4 text-brand-active" />
              Owner:{' '}
              <span className="font-medium text-gray-900">
                {campaign.owner_name}
              </span>
            </span>
          )}
        </div>

        <FBPageInnerTabsContainer>
          <UnderlineTabs<DashboardTab>
            tabs={dashboardTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
            idPrefix="campaign-dashboard"
            aria-label="Campaign sections"
          />
        </FBPageInnerTabsContainer>

        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDashboardOverviewTab
              campaign={campaign}
              campaignId={campaignId}
              campaignStatus={campaignStatus}
              onTabChange={(tab) => setActiveTab(tab as DashboardTab)}
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDashboardTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDashboardContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
              onContactRequested={handleContactRequested}
            />
          )}
          {activeTab === 'deals' && (
            <CampaignDashboardDealsTab campaign={campaign} />
          )}
          {activeTab === 'replies' && (
            <CampaignRepliesTab campaignId={Number(campaignId)} />
          )}
        </div>
      </FBPage>

      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
      <ArchiveCampaignModal
        show={showArchiveConfirm}
        onHide={() => setShowArchiveConfirm(false)}
        campaignName={campaign.name}
        isActive={campaignStatus === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          archiveMutation.mutate(campaignId, {
            onSuccess: () => navigate('/campaigns'),
          })
        }}
        isLoading={archiveMutation.isPending}
      />
    </>
  )
}
